import { Autocomplete, AutocompleteProps, TextFieldProps } from "@mui/material";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { combineClass, IGWidget } from "../GWidget";
import { ISelectRow, TSelectValue } from "../dropdown/Dropdown"
import { IFormFieldProps, useValidator } from "../form/Validators";
import GTextField from "./GTextField";

export interface IGAutoCompleteProps<T extends TSelectValue> extends IGWidget, Omit<IFormFieldProps<T>, "value"|"defaultValue"> {
    value?: ISelectRow<T>
    defaultValue?: ISelectRow<T>
    placeholder?: string
    floatLabel?: string
    options: ISelectRow<T>[]
    textProps?: Omit<TextFieldProps, "error" | "value" | "form" | "defaultValue"> & { defaultValue?:string };
    canClear?:boolean
    hideArrow?:boolean
}

export interface IAutocompleteProps<T extends TSelectValue, FREE_SOLO extends boolean> extends
    Omit<AutocompleteProps<ISelectRow<T>, false, false, FREE_SOLO>, "options" | "onChange" | "renderInput" | "value" | "defaultValue"> { }

export interface IGTextSelectProps<T extends TSelectValue> extends IAutocompleteProps<T, false>, IGAutoCompleteProps<T> {
    onChange?: (e: any, value: ISelectRow<T> | null) => void
}

export function isSelect<T extends TSelectValue>(selected: ISelectRow<T> | TSelectValue | null | undefined): selected is ISelectRow<T> {
    return selected && selected instanceof Object ? true : false;
}
export function getValue<T extends TSelectValue>(value: ISelectRow<T> | TSelectValue | null | undefined): TSelectValue | null {
    return isSelect(value) ? value.value : value || null
}
export function getLabel<T extends TSelectValue>(selected: ISelectRow<T> | null | undefined): string {
    return typeof (selected) == "undefined" || selected === null ? "" : (isSelect(selected) ? selected.label : "" + selected)
}

function _GTextSelect<T extends TSelectValue>(props: IGTextSelectProps<T>, ref:any) {
    const { onChange, floatLabel, textProps, id, className, value, form, controlName, placeholder, canClear, hideArrow, ...acProps } = props
    const [selected,setSelected] = useState<ISelectRow<T>|null>(props.defaultValue || null)
    const [textValue, setTextValue] = useState<string | null>(getLabel(value) || "")
    const { control, error } = useValidator(props)
    const _onSelect = (e: React.SyntheticEvent, value: ISelectRow<T> | null) => {
        setSelected(value)
        onChange?.(e, value)
        if (value) setTextValue(getLabel(value))
        else setTextValue(null)
        control?.update(value)
        form?.setDirty(true)
    }

    const onClose = (_:any,reason:string) => {
        if (reason=="selectOption") return
        control?.setDirty(true, true)
        form?.setDirty(true)
    }

    const _onChange = (e: React.SyntheticEvent, value: string) => setTextValue(value)
    const _isEqual = (option: ISelectRow<T>, value: ISelectRow<T>) => option.value == value.value
    const inputProps: Record<string, any> = {}
    if (error) {
        inputProps.error = true
        inputProps.helperText = error
    }

    useImperativeHandle(ref, () => ({
        get value() { return selected }
    }), [])

    if (hideArrow) {
        if (!acProps.slotProps) acProps.slotProps = {}
        acProps.slotProps.popupIndicator = {sx:{display:"none"}}
    }

    return <Autocomplete className={combineClass(canClear ? "" : "no-clear", className, hideArrow ? "no-arrow" : "")} {...acProps} 
        onChange={_onSelect} onInputChange={_onChange} getOptionLabel={o => o.label} isOptionEqualToValue={_isEqual} onClose={onClose}
        renderInput={(params) => <GTextField {...params} {...textProps} id={id} value={textValue || ""} placeholder={placeholder} floatLabel={floatLabel} {...inputProps} />} />
}

export default forwardRef(_GTextSelect) as <T extends TSelectValue>(props:IGTextSelectProps<T>&{ref?:any}) => JSX.Element